<template>
  <div
    class="fretboard-settings"
    :class="{ 'fretboard-settings--collapsed': isSettingsCollapsed }"
  >
    <!-- Collapse Toggle Button -->
    <button
      class="collapse-toggle"
      :aria-label="
        isSettingsCollapsed ? 'Expand settings' : 'Collapse settings'
      "
      @click="toggleSettingsCollapse"
    >
      {{ isSettingsCollapsed ? "+" : "−" }}
    </button>

    <!-- Left Column: Scale Controls -->
    <div class="left-settings-container">
      <!-- Root and Scale Selector Row -->
      <div class="root-and-scale-selector">
        <div class="select-menus">
          <KeySelector
            class="root-selector"
            @mouseenter="handleRootSelectorMouseEnter"
            @mouseleave="handleRootSelectorMouseLeave"
            @selector-opened="handleRootSelectorOpened"
            @selector-closed="handleRootSelectorClosed"
          />
          <ScaleSelector
            class="scale-selector"
            @mouseenter="showScaleTooltip"
            @mouseleave="hideScaleTooltip"
          />
        </div>
      </div>

      <!-- Markers Toggle Row -->
      <div class="markers-toggle">
        <SmartToggleSwitch
          v-model="isScaleDegree"
          left-label="Degrees"
          right-label="Notes"
          class="markers-toggle-switch"
        />
      </div>
    </div>

    <!-- Divider -->
    <div class="settings-divider"></div>

    <!-- Right Column: Highlight Settings -->
    <div class="right-settings-container">
      <HighlightSettings />
    </div>

    <!-- Simple Settings Tooltip -->
    <TooltipContainer
      :visible="tooltipState.visible"
      :content="tooltipState.content"
      :x="tooltipState.x"
      :y="tooltipState.y"
    />

    <!-- Root Note Selector Tooltip -->
    <ElementTooltip
      :visible="rootTooltip.visible"
      :content="rootTooltip.content"
      :target-element="rootTooltip.targetElement"
      :position="'above'"
    />

    <!-- Scale Selector Tooltip -->
    <ElementTooltip
      :visible="scaleTooltip.visible"
      :content="scaleTooltip.content"
      :target-element="scaleTooltip.targetElement"
      :position="'above'"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useFretboardStore } from "@/stores/fretboard";
import { storeToRefs } from "pinia";
import KeySelector from "./KeySelector.vue";
import ScaleSelector from "./ScaleSelector.vue";
import SmartToggleSwitch from "./SmartToggleSwitch.vue";
import HighlightSettings from "./HighlightSettings.vue";
import TooltipContainer from "./TooltipContainer.vue";
import { useSimpleTooltip } from "@/composables/useSimpleTooltip";
import ElementTooltip from "./ElementTooltip.vue";
import { useGlobalTooltip } from "@/composables/useGlobalTooltip";
import { onMounted, onUnmounted } from "vue";

const store = useFretboardStore();
const { isSettingsCollapsed } = storeToRefs(store);

const isScaleDegree = computed({
  get: () => store.isScaleDegree,
  set: (value: boolean) => store.setIsScaleDegree(value),
});

function toggleSettingsCollapse() {
  store.setSettingsCollapsed(!isSettingsCollapsed.value);
}

// Initialize simple tooltip system (for any remaining simple tooltips)
const { state: tooltipState } = useSimpleTooltip();

// Global tooltip coordination
const {
  registerTooltip,
  unregisterTooltip,
  showTooltip: showGlobalTooltip,
  hideTooltip: hideGlobalTooltip,
} = useGlobalTooltip();

// Element tooltips for positioned tooltips above elements
const rootTooltip = ref({
  visible: false,
  content: "",
  targetElement: null as HTMLElement | null,
});

const scaleTooltip = ref({
  visible: false,
  content: "",
  targetElement: null as HTMLElement | null,
});

// Track if root selector is open to prevent hover events
const isRootSelectorOpen = ref(false);

// Tooltip functions for root note selector
function showRootTooltip(event: MouseEvent) {
  const target = event.currentTarget as HTMLElement;
  rootTooltip.value.content = "Change root";
  rootTooltip.value.targetElement = target;
  rootTooltip.value.visible = true;
  showGlobalTooltip("fretboard-settings-root");
}

function hideRootTooltip() {
  rootTooltip.value.visible = false;
  hideGlobalTooltip("fretboard-settings-root");
}

// New handlers that respect the open state
function handleRootSelectorMouseEnter(event: MouseEvent) {
  // Only show tooltip if selector is not open
  if (!isRootSelectorOpen.value) {
    showRootTooltip(event);
  }
}

function handleRootSelectorMouseLeave() {
  // Only hide tooltip if selector is not open
  if (!isRootSelectorOpen.value) {
    hideRootTooltip();
  }
}

function handleRootSelectorOpened() {
  isRootSelectorOpen.value = true;
  hideRootTooltip(); // Make sure tooltip is hidden
}

function handleRootSelectorClosed() {
  isRootSelectorOpen.value = false;
}

// Tooltip functions for scale selector
function showScaleTooltip(event: MouseEvent) {
  const target = event.currentTarget as HTMLElement;
  scaleTooltip.value.content = "Change scale";
  scaleTooltip.value.targetElement = target;
  scaleTooltip.value.visible = true;
  showGlobalTooltip("fretboard-settings-scale");
}

function hideScaleTooltip() {
  scaleTooltip.value.visible = false;
  hideGlobalTooltip("fretboard-settings-scale");
}

// Register tooltips with global system
onMounted(() => {
  registerTooltip("fretboard-settings-root", hideRootTooltip);
  registerTooltip("fretboard-settings-scale", hideScaleTooltip);
});

onUnmounted(() => {
  unregisterTooltip("fretboard-settings-root");
  unregisterTooltip("fretboard-settings-scale");
});
</script>

<style scoped lang="scss">
.fretboard-settings {
  /* Settings Container */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 24px 0px 24px 32px;
  gap: 32px;

  position: relative;
  width: 100%;
  // min-height: 208px;

  background: var(--shade-20);
  border-radius: 0px 0px 32px 32px;
  margin: 0 auto;

  transition: all 0.36s ease-out;
  height: auto; /* Default expanded height */
  min-height: 160px; /* Minimum height for expanded state */
}

/* Collapsed State */
.fretboard-settings--collapsed {
  height: 72px !important; /* Fixed height for collapsed state */
  min-height: 72px !important;
  overflow: hidden; /* Hide content that doesn't fit */

  /* Reduce overall padding */
  padding: 16px 0px 16px 32px;

  /* Hide the markers toggle */
  .markers-toggle {
    opacity: 0;
    transform: translateY(-8px);
    pointer-events: none;
  }

  /* Make root note and scale selectors more compact */
  .root-selector,
  .scale-selector {
    height: 32px !important;

    :deep(.root-selector-trigger) {
      height: 32px;
      width: 32px;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;

      .note-label {
        font-size: 12px !important;
        width: 20px !important;
        height: 20px !important;

        svg {
          width: 20px !important;
          height: 20px !important;
          font-size: 12px !important;
        }

        text {
          font-size: 12px !important;
        }
      }
    }

    :deep(.scale-select) {
      height: 32px;
      font-size: 12px;
      padding: 6px 10px;
    }
  }

  .root-selector {
    width: 32px !important;
  }

  .select-menus {
    height: 32px;
  }

  .left-settings-container {
    margin-top: 0;
    gap: 16px;
  }
}

/* Collapse Toggle Button */
.collapse-toggle {
  position: absolute;
  top: 16px;
  right: 24px;
  width: 32px;
  height: 32px;
  background: var(--shade-10);
  border: 1px solid var(--shade-30);
  border-radius: 16px;
  color: var(--shade-60);
  font-size: 20px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-out;
  z-index: 10;

  &:hover {
    background: var(--shade-20);
    color: var(--shade-70);
    border-color: var(--shade-40);
  }

  &:focus-visible {
    outline: 2px solid var(--light-48);
    outline-offset: 2px;
  }
}

.left-settings-container {
  /* Left-settings-container */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 24px;
  margin-top: 12px;
  flex: none;
  order: 0;
  flex-grow: 0;
}

.root-and-scale-selector {
  /* Root and Scale Selector */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0px;

  flex: none;
  order: 0;
  flex-grow: 0;
  transition: all 0.36s ease-out;
}

.select-menus {
  /* Select Menus */
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  padding: 0px;
  gap: 8px;

  width: 186px;
  height: 44px;

  flex: none;
  order: 1;
  flex-grow: 0;
  transition: all 0.36s ease-out;
}

.markers-toggle {
  /* Markers Toggle */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0px;

  flex: none;
  order: 1;
  flex-grow: 0;

  transition: all 0.36s ease-out;
}

.settings-divider {
  width: 2px;
  height: 100%;
  background: var(--shade-10);
  flex-shrink: 0;
}

.right-settings-container {
  flex: 1;
  display: flex;
  justify-content: flex-start;
  overflow-x: auto;
  scrollbar-width: thin; /* Firefox - thin scrollbar */
  scrollbar-color: transparent transparent; /* Firefox - hidden by default */
  padding: 4px 0 8px;
}

/* Custom WebKit Scrollbars for Settings - Hidden by default, appear on hover */
.right-settings-container::-webkit-scrollbar {
  height: 8px;
}

.right-settings-container::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 4px;
}

.right-settings-container::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

/* Show scrollbar on hover */
.right-settings-container:hover {
  scrollbar-color: var(--shade-40) var(--shade-30); /* Firefox */
}

.right-settings-container:hover::-webkit-scrollbar-thumb {
  background: var(--shade-40);
}

.right-settings-container:hover::-webkit-scrollbar-track {
  background: var(--shade-30);
}

/* Progressive Responsive Design */

/* Large screens: Reduce gap slightly */
@media (max-width: 1200px) {
  .fretboard-settings {
    gap: 28px;
    padding: 24px 0 24px 28px;
  }
}

/* Medium-large screens: Start to prepare for layout change */
@media (max-width: 900px) {
  .fretboard-settings {
    gap: 24px;
    // padding: 20px 0 20px 24px;
  }
}

/* Medium screens: Change to vertical layout, settings divider hidden */
@media (max-width: 800px) {
  .fretboard-settings {
    flex-direction: column;
    gap: 20px;
    // padding: 20px 0 20px 24px;
  }

  .left-settings-container {
    width: 100%;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 16px;
    padding-right: 24px;
    margin-top: 0;
  }

  .root-and-scale-selector {
    flex-direction: row;
    align-items: center;
    width: auto;
    height: auto;
    gap: 12px;
  }

  .markers-toggle {
    flex-direction: row;
    align-items: center;
    width: auto;
    height: auto;
    gap: 12px;
  }

  .settings-divider {
    display: none;
  }

  .right-settings-container {
    width: 100%;
    justify-content: left;
  }
}

/* Small screens: Reduce gaps and button sizes */
@media (max-width: 640px) {
  .fretboard-settings {
    gap: 16px;
    padding: 16px 0 16px 20px;
  }

  .left-settings-container {
    gap: 12px;
  }

  .root-and-scale-selector,
  .markers-toggle {
    gap: 8px;
  }

  .smart-toggle-switch.markers-toggle-switch {
    height: 40px;
  }
  .smart-toggle-switch.markers-toggle-switch :deep(.toggle-background-slider) {
    height: 32px !important;
  }

  .root-selector :deep(.root-selector-trigger),
  .root-selector {
    height: 40px;
    width: 40px;
  }
  .scale-selector :deep(.scale-select) {
    height: 40px;
  }
}

/* Extra small screens: Stack left settings vertically, minimal spacing */
@media (max-width: 480px) {
  .fretboard-settings {
    gap: 12px;
    padding: 12px 0 12px 16px;
  }

  .left-settings-container {
    flex-direction: column;
    gap: 12px;
  }

  .root-and-scale-selector,
  .markers-toggle {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
